// components/features/orders/OrderRow.tsx
import { Order } from "@cabukaka/types";
import TableRow from "../../ui/TableRow";
import { Package } from "lucide-react";

interface OrderRowProps {
  order: Order;
  onAction: (order: Order) => void;
  onSelect?: (order: Order, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function OrderRow({ 
  order, 
  onAction, 
  onSelect,
  isSelected = false,
  selectable = false
}: OrderRowProps) {
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      pending: { label: "En attente", className: "bg-yellow-100 text-yellow-700" },
      confirmed: { label: "Confirmée", className: "bg-blue-100 text-blue-700" },
      preparing: { label: "Préparation", className: "bg-purple-100 text-purple-700" },
      shipped: { label: "Expédiée", className: "bg-indigo-100 text-indigo-700" },
      delivered: { label: "Livrée", className: "bg-green-100 text-green-700" },
      cancelled: { label: "Annulée", className: "bg-red-100 text-red-700" }
    };
    const statusInfo = statusMap[status] || { label: status, className: "bg-gray-100 text-gray-700" };
    return <span className={`px-2 py-1 text-xs rounded-full ${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      pending: { label: "En attente", className: "bg-yellow-100 text-yellow-700" },
      paid: { label: "Payé", className: "bg-green-100 text-green-700" },
      failed: { label: "Échoué", className: "bg-red-100 text-red-700" },
      refunded: { label: "Remboursé", className: "bg-gray-100 text-gray-700" }
    };
    const statusInfo = statusMap[status] || { label: status, className: "bg-gray-100 text-gray-700" };
    return <span className={`px-2 py-1 text-xs rounded-full ${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  return (
    <TableRow
      item={order}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      {/* Colonne Référence - toujours visible */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Package size={16} className="text-gray-400" />
          <span className="font-mono text-sm font-medium text-gray-800">{order.reference}</span>
        </div>
      </td>

      {/* Colonne Client - cachée sur mobile */}
      <td className="hidden sm:table-cell py-3 px-4">
        <span className="text-gray-700">{order.client?.name || "-"}</span>
      </td>

      {/* Colonne Total - cachée sur mobile */}
      <td className="hidden sm:table-cell py-3 px-4">
        <span className="font-semibold text-gray-900">{order.total.toLocaleString('fr-FR')} FCFA</span>
      </td>

      {/* Colonne Statut commande */}
      <td className="py-3 px-4">
        {getStatusBadge(order.status)}
      </td>

      {/* Colonne Paiement - cachée sur tablette */}
      <td className="hidden md:table-cell py-3 px-4">
        {getPaymentStatusBadge(order.paymentStatus)}
      </td>

      {/* Colonne Date - cachée sur tablette */}
      <td className="hidden lg:table-cell py-3 px-4">
        <span className="text-gray-500 text-sm">
          {new Date(order.createdAt).toLocaleDateString('fr-FR')}
        </span>
      </td>
    </TableRow>
  );
}